'use client';

import { type ChangeEvent, useCallback } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Delete02Icon } from '@terra/ui-icons';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { BodyDefault } from '../../../components/typography';
import { isInvalidDocumentationUrlInput } from './documentationUrls.utils';

type DocumentationUrlInputRowProps = {
  url: string;
  index: number;
  placeholder: string;
  invalidUrlMessage: string;
  removeAriaLabel: string;
  onChange: ({ index, value }: { index: number; value: string }) => void;
  onRemove: ({ index }: { index: number }) => void;
};

export const DocumentationUrlInputRow = ({
  url,
  index,
  placeholder,
  invalidUrlMessage,
  removeAriaLabel,
  onChange,
  onRemove,
}: DocumentationUrlInputRowProps) => {
  const showUrlError = isInvalidDocumentationUrlInput(url);
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange({ index, value: event.target.value });
    },
    [index, onChange],
  );
  const handleRemove = useCallback(() => {
    onRemove({ index });
  }, [index, onRemove]);
  const isFirstInput = index === 0;

  return (
    <HStack gap={2} alignItems="flex-start" w="full">
      <VStack alignItems="stretch" gap={1} flex={1} minW={0}>
        <Input
          type="url"
          inputMode="url"
          autoComplete="url"
          placeholder={placeholder}
          value={url}
          onChange={handleInputChange}
          borderColor={showUrlError ? 'border.error' : undefined}
          w="full"
        />
        {showUrlError ? (
          <BodyDefault color="fg.error">{invalidUrlMessage}</BodyDefault>
        ) : null}
      </VStack>
      <IconButton
        variant="ghost"
        size="sm"
        flexShrink={0}
        color="fg.neutral.default"
        visibility={isFirstInput ? 'hidden' : 'visible'}
        pointerEvents={isFirstInput ? 'none' : 'auto'}
        tabIndex={isFirstInput ? -1 : undefined}
        aria-hidden={isFirstInput}
        aria-label={removeAriaLabel}
        onClick={handleRemove}
      >
        <Delete02Icon size={16} />
      </IconButton>
    </HStack>
  );
};
